import React, { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { CrmField, DraggableCrmField } from './DraggableCrmField';
import { ContactRecord } from 'src/services/integration-app.service';
import { Label } from 'src/components/Label';

interface DropZoneProps {
  campaignField: string;
  fieldLabel: string;
  mappedField: CrmField | null;
  disabled?: boolean;
  sampleContacts?: ContactRecord[];
  required?: boolean;
  onFieldMapped?: (campaignField: string, crmFieldValue: string) => void;
  onFieldRemoved?: (campaignField: string) => void;
}

export const DropZone: React.FC<DropZoneProps> = ({
  campaignField,
  fieldLabel,
  mappedField,
  disabled = false,
  sampleContacts = [],
  required = false,
  onFieldMapped,
  onFieldRemoved,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const draggedFieldValue = e.dataTransfer.getData('text/plain');

    setIsDragOver(false);

    if (draggedFieldValue && onFieldMapped) {
      onFieldMapped(campaignField, draggedFieldValue);
    }
  };

  const handleRemoveMapping = () => {
    if (onFieldRemoved) {
      onFieldRemoved(campaignField);
    }
  };

  const getDropZoneClasses = () => {
    const baseClasses =
      'relative border-dashed rounded-lg p-3 transition-all duration-200 h-[120px]';

    if (isDragOver && !mappedField) {
      return `${baseClasses} border-4 border-primary-400 bg-primary-100 scale-[1.02]`;
    }

    if (mappedField) {
      return `relative group h-[120px]`;
    }

    if (required) {
      return `${baseClasses} border-2 border-error-300 hover:border-error-400 hover:bg-error-50`;
    }

    return `${baseClasses} border-2 border-neutral-300 hover:border-primary-300 hover:bg-primary-50`;
  };

  return (
    <div className="space-y-2">
      <Label
        required={required}
        className="text-base text-neutral-600 font-bold mb-0"
      >
        {fieldLabel}
      </Label>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={getDropZoneClasses()}
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {mappedField ? (
          <>
            <DraggableCrmField
              field={mappedField}
              sampleContacts={sampleContacts}
              sourceCampaignField={campaignField}
            />
            <button
              onClick={handleRemoveMapping}
              className="absolute top-2 right-2 text-neutral-400 hover:text-error-600 text-sm p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-neutral-0 shadow-sm border border-neutral-200"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <div
            className={`flex items-center justify-center text-sm transition-colors h-full ${
              isDragOver
                ? 'text-primary-600 font-medium'
                : required
                  ? 'text-error-500'
                  : 'text-neutral-400'
            }`}
          >
            {isDragOver ? 'Drop' : 'Drag'} field here
          </div>
        )}
      </div>
    </div>
  );
};
